import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { COUNTRIES, countryCodeToFlag } from '@wlb/shared/constants/countries';
import profileService from '@wlb/shared/api/services/profileService';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import { kgToLbs, lbsToKg } from '@wlb/shared/utils/weightUtils';
import type { AIBuddy, BuddyMatchingItem, BuddySearchRequest, BuddySearchResultUser } from '@wlb/shared/types';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import useChooseAiBuddy from '@wlb/shared/hooks/useChooseAiBuddy';
import { ChevronDownIcon, FilterIcon, SearchIcon } from '../components/ui/icons';
import { ListStatus } from '../components/ui/ListStatus';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { InfiniteScrollFooter } from '../components/ui/InfiniteScrollFooter';
import { ScrollToTopButton } from '../components/ui/ScrollToTopButton';
import { avatarFallback } from '../components/ui/avatarFallback';
import '../components/ui/avatar.css';
import { ScaleBar } from '../components/ui/ScaleBar';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { IS_DEV } from '../config/environment';
import { useOpenDirectConversation } from '../features/messages/useOpenDirectConversation';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import {
  getBuddyRequestButtonState,
  rememberBuddyRequest,
} from '../features/profile/sentBuddyRequests';
import '../features/more/more.css';
import './Buddies.css';

const AGE_MIN = 18;
const AGE_MAX = 75;
const AGE_TICKS = ['18', '25', '35', '45', '65', '75+'] as const;
const STORAGE_KEY = 'buddies-search-state';

/** Advanced-filter bounds and defaults, matching mobile's buddiesSearch screen. */
const WEIGHT_MIN_KG = 36;
const WEIGHT_MAX_KG = 127;
const DEFAULT_WEIGHT_KG: [number, number] = [78, 111];
const LOSE_MIN_KG = 2;
const LOSE_MAX_KG = 27;
const DEFAULT_LOSE_KG = 18;
const DEFAULT_AGE: [number, number] = [25, 45];

/** Result rows revealed per scroll page (mobile buddySearchResult PAGE_SIZE). */
const RESULTS_PAGE_SIZE = 10;

type Gender = 'All' | 'Male' | 'Female' | 'Other';
type WeightUnit = 'kg' | 'lbs';
type LoseMode = 'any' | 'range';

/** Evenly spaced tick captions for a slider scale. */
const scaleTicks = (min: number, max: number, count: number): string[] =>
  Array.from({ length: count }, (_, index) =>
    String(Math.round(min + ((max - min) * index) / (count - 1))),
  );

interface BuddiesSearchState {
  search: string;
  gender: Gender;
  ageMin: number;
  ageMax: number;
  countryId?: number;
  weightKg?: [number, number];
  weightUnit?: WeightUnit;
  loseMode?: LoseMode;
  loseKg?: number;
  loseUnit?: WeightUnit;
  matches: BuddySearchResultUser[];
  showResults: boolean;
}

interface StaticBuddy {
  id: number;
  name: string;
  bio: string;
  age: number;
  country: string;
  weight: string;
  match: number;
  userGuid: string;
}

/** Maps a BuddyMatchingItem (AI buddy matching API) to the AIBuddy shape useChooseAiBuddy expects. */
const mapAiMatchToBuddy = (m: BuddyMatchingItem): AIBuddy => ({
  id: String(m.buddyId ?? ''),
  agentGuid: m.userGuid ?? m.buddyId ?? '',
  profileUserGuid: m.userGuid ?? m.buddyId ?? '',
  name: m.name ?? '',
  role: m.about ?? '',
  description: m.description ?? '',
  about: m.about ?? '',
  image: m.profilePictureUrl ?? '',
  age: m.age ?? 0,
  country: '',
  goalLbs: 0,
  specialties: [],
});

/** Design placeholders; only rendered in dev so users never see them as real matches. */
const STATIC_BUDDIES: StaticBuddy[] = [
  { id: 1, name: 'David J.', bio: 'Similar goal, prefers daily check-ins, and wants motivation support.', age: 32, country: 'United States', weight: '40 lbs', match: 96, userGuid: 'static-buddy-1' },
  { id: 2, name: 'David J.', bio: 'Similar goal, prefers daily check-ins, and wants motivation support.', age: 32, country: 'United States', weight: '40 lbs', match: 92, userGuid: 'static-buddy-2' },
  { id: 3, name: 'David J.', bio: 'Similar goal, prefers daily check-ins, and wants motivation support.', age: 32, country: 'United States', weight: '40 lbs', match: 94, userGuid: 'static-buddy-3' },
  { id: 4, name: 'David J.', bio: 'Similar goal, prefers daily check-ins, and wants motivation support.', age: 32, country: 'United States', weight: '40 lbs', match: 97, userGuid: 'static-buddy-4' },
  { id: 5, name: 'David J.', bio: 'Similar goal, prefers daily check-ins, and wants motivation support.', age: 32, country: 'United States', weight: '40 lbs', match: 97, userGuid: 'static-buddy-5' },
];

interface UnitToggleProps {
  unit: WeightUnit;
  /** Names the group so the two symbols are not read on their own. */
  label: string;
  onChange: (unit: WeightUnit) => void;
}

function UnitToggle({ unit, label, onChange }: UnitToggleProps) {
  const { t } = useTranslation();

  return (
    <div className="buddies-page__unit-toggle" role="group" aria-label={label}>
      {(['kg', 'lbs'] as WeightUnit[]).map(option => (
        <button
          key={option}
          className={`buddies-page__unit${unit === option ? ' buddies-page__unit--selected' : ''}`}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={unit === option}
        >
          {t(option === 'kg' ? 'basicInfo.unitShortKg' : 'basicInfo.unitShortLbs')}
        </button>
      ))}
    </div>
  );
}

export function Buddies() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openUserProfile = useOpenUserProfile();
  const { userRelation } = useProfile();
  const { openConversation, openingUserId } = useOpenDirectConversation();
  const { isCurrent, currentAgentId, pendingBuddyId, choose } = useChooseAiBuddy();
  const [requestingUserId, setRequestingUserId] = useState<number | null>(null);
  const [aiTarget, setAiTarget] = useState<AIBuddy | null>(null);
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<Gender>('All');
  const [ageMin, setAgeMin] = useState(25);
  const [ageMax, setAgeMax] = useState(45);
  const [countryId, setCountryId] = useState<number | undefined>();
  const [countrySearch, setCountrySearch] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  // Weights are held in kg whatever the toggle shows, so a unit switch never
  // rewrites the filter the API is given.
  const [weightKg, setWeightKg] = useState<[number, number]>(DEFAULT_WEIGHT_KG);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>('lbs');
  const [loseMode, setLoseMode] = useState<LoseMode>('any');
  const [loseKg, setLoseKg] = useState(DEFAULT_LOSE_KG);
  const [loseUnit, setLoseUnit] = useState<WeightUnit>('lbs');
  const countryFieldRef = useRef<HTMLFieldSetElement>(null);
  const searchRequestIdRef = useRef(0);
  const [matches, setMatches] = useState<BuddySearchResultUser[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  /** Kept so a failed search can be retried with exactly the same criteria. */
  const lastSearchRef = useRef<BuddySearchRequest | null>(null);
  const [aiMode, setAiMode] = useState(false);
  const [aiMatches, setAiMatches] = useState<BuddyMatchingItem[]>([]);
  const [aiError, setAiError] = useState<string | null>(null);

  // Restore search state on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state: BuddiesSearchState = JSON.parse(saved);
        setSearch(state.search);
        setGender(state.gender);
        setAgeMin(state.ageMin);
        setAgeMax(state.ageMax);
        setCountryId(state.countryId);
        if (state.weightKg) setWeightKg(state.weightKg);
        if (state.weightUnit) setWeightUnit(state.weightUnit);
        if (state.loseMode) setLoseMode(state.loseMode);
        if (state.loseKg != null) setLoseKg(state.loseKg);
        if (state.loseUnit) setLoseUnit(state.loseUnit);
        setMatches(state.matches);
        setShowResults(state.showResults);
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Save search state when it changes
  useEffect(() => {
    if (showResults && matches.length > 0) {
      const state: BuddiesSearchState = {
        search,
        gender,
        ageMin,
        ageMax,
        countryId,
        weightKg,
        weightUnit,
        loseMode,
        loseKg,
        loseUnit,
        matches,
        showResults,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [
    search,
    gender,
    ageMin,
    ageMax,
    countryId,
    weightKg,
    weightUnit,
    loseMode,
    loseKg,
    loseUnit,
    matches,
    showResults,
  ]);

  const filteredCountries = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    return query
      ? COUNTRIES.filter(country => country.label.toLowerCase().includes(query))
      : COUNTRIES;
  }, [countrySearch]);

  const selectedCountry = COUNTRIES.find(country => country.id === countryId);

  const isLbsWeight = weightUnit === 'lbs';
  const isLbsLose = loseUnit === 'lbs';
  const weightBounds = isLbsWeight
    ? { min: kgToLbs(WEIGHT_MIN_KG), max: kgToLbs(WEIGHT_MAX_KG) }
    : { min: WEIGHT_MIN_KG, max: WEIGHT_MAX_KG };
  const loseBounds = isLbsLose
    ? { min: kgToLbs(LOSE_MIN_KG), max: kgToLbs(LOSE_MAX_KG) }
    : { min: LOSE_MIN_KG, max: LOSE_MAX_KG };
  const weightDisplay: [number, number] = isLbsWeight
    ? [kgToLbs(weightKg[0]), kgToLbs(weightKg[1])]
    : weightKg;
  const loseDisplay = isLbsLose ? kgToLbs(loseKg) : loseKg;
  const weightUnitLabel = t(isLbsWeight ? 'basicInfo.unitShortLbs' : 'basicInfo.unitShortKg');
  const loseUnitLabel = t(isLbsLose ? 'basicInfo.unitShortLbs' : 'basicInfo.unitShortKg');

  const weightTicks = useMemo(
    () => scaleTicks(weightBounds.min, weightBounds.max, 6),
    [weightBounds.min, weightBounds.max],
  );
  const loseTicks = useMemo(
    () => scaleTicks(loseBounds.min, loseBounds.max, 7),
    [loseBounds.min, loseBounds.max],
  );

  const activeFilterCount = [
    gender !== 'All',
    ageMin !== DEFAULT_AGE[0] || ageMax !== DEFAULT_AGE[1],
    countryId != null,
    weightKg[0] !== DEFAULT_WEIGHT_KG[0] || weightKg[1] !== DEFAULT_WEIGHT_KG[1],
    loseMode === 'range',
  ].filter(Boolean).length;

  const results = useMemo<(BuddySearchResultUser | StaticBuddy)[]>(
    () => (matches.length > 0 ? matches : IS_DEV ? STATIC_BUDDIES : []),
    [matches],
  );

  /**
   * The search answers with the whole match list, so paging here is purely a
   * render budget: rows are revealed a page at a time as the member scrolls
   * rather than mounting hundreds of cards at once (mobile buddySearchResult
   * parity).
   */
  const [visibleCount, setVisibleCount] = useState(RESULTS_PAGE_SIZE);
  /**
   * One render long, so `useInfiniteScroll` sees the same start/finish it would
   * get from a network page and re-arms instead of treating the growth as a
   * stall.
   */
  const [expanding, setExpanding] = useState(false);

  useEffect(() => { setVisibleCount(RESULTS_PAGE_SIZE); }, [matches]);

  useEffect(() => {
    if (!expanding) return;
    setVisibleCount(count => count + RESULTS_PAGE_SIZE);
    setExpanding(false);
  }, [expanding]);

  const visibleResults = useMemo(
    () => results.slice(0, visibleCount),
    [results, visibleCount],
  );
  const hasMoreResults = visibleCount < results.length;

  useEffect(() => {
    if (!countryOpen) {
      return;
    }

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (countryFieldRef.current && !countryFieldRef.current.contains(event.target as Node)) {
        setCountryOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [countryOpen]);

  /**
   * Only the newest search may write results: submitting twice in a row would
   * otherwise let a slower earlier response land on top of the later one.
   */
  const runSearch = async (payload: BuddySearchRequest) => {
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    lastSearchRef.current = payload;
    setLoading(true);
    setSearchError(null);
    try {
      const response = await profileService.buddySearch(payload);
      if (searchRequestIdRef.current === requestId) {
        setMatches(response.data.buddies ?? []);
      }
    } catch (err) {
      // A failed search is reported as a failure; clearing the list here would
      // read as "nobody matches you".
      if (searchRequestIdRef.current === requestId) {
        setMatches([]);
        setSearchError(getUserFacingErrorMessage(err, t('common.errors.generic')));
      }
    } finally {
      if (searchRequestIdRef.current === requestId) {
        setShowResults(true);
        setLoading(false);
      }
    }
  };

  /**
   * Mobile treats a username as its own search: the filters are dropped so a
   * name that sits outside the current age/weight bands is still findable.
   */
  const searchByName = () => {
    const userName = search.trim();
    if (!userName) return;
    runSearch({
      userName,
      currentWeightScaleUnit: 1,
      weightToLoseScaleUnit: 1,
    }).catch(() => undefined);
  };

  const searchByFilters = () => {
    const payload: BuddySearchRequest = {
      ageFrom: ageMin,
      ageTo: ageMax,
      currentWeightFrom: weightUnit === 'lbs' ? kgToLbs(weightKg[0]) : weightKg[0],
      currentWeightTo: weightUnit === 'lbs' ? kgToLbs(weightKg[1]) : weightKg[1],
      currentWeightScaleUnit: weightUnit === 'lbs' ? 2 : 1,
      wantToLose: loseMode === 'range' ? (loseUnit === 'lbs' ? kgToLbs(loseKg) : loseKg) : null,
      weightToLoseScaleUnit: loseUnit === 'lbs' ? 2 : 1,
    };
    if (gender !== 'All') payload.gender = gender === 'Male' ? 1 : gender === 'Female' ? 2 : 3;
    if (countryId) payload.country = [countryId];

    return runSearch(payload).catch(() => undefined);
  };

  const showMatches = () => {
    searchByFilters();
  };

  const requestBuddy = async (user: BuddySearchResultUser) => {
    setRequestingUserId(user.userId);
    try {
      await userRelation(user.userId, 'BuddyInvite');
      rememberBuddyRequest(user.userId);
      setMatches(previous =>
        previous.map(item =>
          item.userId === user.userId
            ? { ...item, invited: true, isRequested: true, status: 'pending' }
            : item,
        ),
      );
      ToastService.show({ type: 'success', message: t('profile.toast.requestSent') });
    } catch {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setRequestingUserId(null);
    }
  };

  const handleAiSuggest = async () => {
    setAiMode(true);
    setAiError(null);
    setMatches([]);
    setSearchError(null);
    setLoading(true);
    setShowResults(true);
    try {
      const response = await profileService.buddyMatching({ buddyTypeFilter: 'AIBuddy' });
      setAiMatches(response.data.matches ?? []);
    } catch (err) {
      setAiMatches([]);
      setAiError(getUserFacingErrorMessage(err, t('common.errors.generic')));
    } finally {
      setLoading(false);
    }
  };

  const confirmAiChoice = async () => {
    if (!aiTarget) return;
    const result = await choose(aiTarget);
    setAiTarget(null);
    if (!result.success) {
      ToastService.show({
        type: 'error',
        message: result.message || t('common.errors.generic'),
      });
    }
  };

  const renderAiConfirmModal = () => {
    if (!aiTarget) return null;

    const unfollowing = isCurrent(aiTarget);
    const switching = !unfollowing && currentAgentId != null;

    const prompt = unfollowing
      ? {
          title: t('profile.unfollow.title', { name: aiTarget.name }),
          message: t('profile.unfollow.description', { name: aiTarget.name }),
          confirmLabel: t('profile.unfollow.confirm'),
          cancelLabel: t('common.buttons.cancel'),
        }
      : switching
        ? {
            title: t('aiBuddy.changeBuddy.title'),
            message: t('aiBuddy.changeBuddy.description'),
            confirmLabel: t('aiBuddy.changeBuddy.confirm'),
            cancelLabel: t('aiBuddy.changeBuddy.cancel'),
          }
        : {
            title: t('aiBuddy.modal.followBuddyConfirmTitle'),
            message: t('aiBuddy.modal.followBuddyConfirmDesc'),
            confirmLabel: t('aiBuddy.details.chooseButton'),
            cancelLabel: t('common.buttons.cancel'),
          };

    return (
      <ConfirmModal
        title={prompt.title}
        message={prompt.message}
        confirmLabel={prompt.confirmLabel}
        cancelLabel={prompt.cancelLabel}
        mediaUrl={aiTarget.image ? resolveProfilePictureUrl(aiTarget.image) : null}
        loading={pendingBuddyId === aiTarget.id}
        onClose={() => setAiTarget(null)}
        onConfirm={() => { confirmAiChoice().catch(() => undefined); }}
      />
    );
  };

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
      setAiMode(false);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
      navigate(-1);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setGender('All');
    setAgeMin(DEFAULT_AGE[0]);
    setAgeMax(DEFAULT_AGE[1]);
    setCountryId(undefined);
    setCountrySearch('');
    setWeightKg(DEFAULT_WEIGHT_KG);
    setWeightUnit('lbs');
    setLoseMode('any');
    setLoseKg(DEFAULT_LOSE_KG);
    setLoseUnit('lbs');
  };

  return (
    <section className="buddies-page" aria-labelledby="buddies-title">
      <header className="buddies-page__header">
        <button
          className="buddies-page__back"
          type="button"
          onClick={handleBack}
          aria-label={t('common.buttons.close')}
        >
          ‹
        </button>
        <h1 id="buddies-title">{t('home.buddies.title')}</h1>
      </header>

      <form
        className="buddies-page__search-row"
        role="search"
        onSubmit={event => {
          event.preventDefault();
        }}
      >
        <label className="buddies-page__search">
          <span className="sr-only">{t('home.buddies.searchPlaceholder')}</span>
          {/* Not type="search": the browser adds its own clear control, which
              would sit next to the one below. */}
          <input
            type="text"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('home.buddies.searchPlaceholder')}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault(); // Prevent accidental form submission via Enter key
              }
            }}
          />
          <button
            className="buddies-page__search-submit"
            type="button"
            disabled={search.trim().length === 0}
            onClick={() => {
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              searchByName();
            }}
            aria-label={t('home.buddies.searchPlaceholder')}
          >
            <SearchIcon size={18} />
          </button>
        </label>
        <button
          className="buddies-page__filter-button"
          type="button"
          onClick={() => setShowResults(false)}
          aria-label={t('home.buddies.accessibility.editFilters')}
        >
          <FilterIcon size={20} />
          {activeFilterCount > 0 && (
            <span className="buddies-page__filter-count">{activeFilterCount}</span>
          )}
        </button>
      </form>

      {!showResults && <div className="buddies-page__filters">
        <fieldset className="buddies-page__field">
          <legend>{t('home.buddies.gender')}</legend>
          <div className="buddies-page__chips">
            {(['All', 'Male', 'Female', 'Other'] as Gender[]).map(option => (
              <button
                key={option}
                className={`buddies-page__chip${gender === option ? ' buddies-page__chip--selected' : ''}`}
                type="button"
                onClick={() => setGender(option)}
                aria-pressed={gender === option}
              >
                {t(`home.buddies.${option.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="buddies-page__field">
          <legend>{t('home.buddies.buddyAge')}</legend>
          <ScaleBar
            mode="range"
            min={AGE_MIN}
            max={AGE_MAX}
            value={[ageMin, ageMax]}
            onChange={([low, high]) => { setAgeMin(low); setAgeMax(high); }}
            minAriaLabel={`${t('home.buddies.buddyAge')} ${t('common.minimum')}`}
            maxAriaLabel={`${t('home.buddies.buddyAge')} ${t('common.maximum')}`}
            ticks={AGE_TICKS}
          />
        </fieldset>

        <fieldset ref={countryFieldRef} className="buddies-page__field">
          <legend>{t('home.buddies.country')}</legend>
          <button className="buddies-page__country-trigger" type="button" onClick={() => setCountryOpen(open => !open)} aria-expanded={countryOpen}>
            <span>{selectedCountry ? `${countryCodeToFlag(selectedCountry.value)} ${selectedCountry.label}` : t('home.buddies.allCountries')}</span>
            <ChevronDownIcon size={16} />
          </button>
          {countryOpen && (
            <div className="buddies-page__country-menu">
              <strong>{t('home.buddies.chooseCountry')}</strong>
              <label className="buddies-page__country-search">
                <span className="sr-only">{t('home.buddies.searchCountry')}</span>
                <input value={countrySearch} onChange={event => setCountrySearch(event.target.value)} placeholder={t('home.buddies.searchCountry')} autoFocus />
                <SearchIcon size={16} />
              </label>
              <div className="buddies-page__country-list">
                {filteredCountries.map(country => (
                  <button
                    type="button"
                    key={country.id}
                    className={`buddies-page__country-option${country.id === countryId ? ' buddies-page__country-option--selected' : ''}`}
                    onClick={() => {
                      setCountryId(country.id === countryId ? undefined : country.id);
                      setCountryOpen(false);
                    }}
                  >
                    <span>{countryCodeToFlag(country.value)} {country.label}</span>
                    {country.id === countryId && <span aria-hidden>●</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </fieldset>

        <div className="buddies-page__advanced">
          <button
            className="buddies-page__accordion"
            type="button"
            onClick={() => setAdvancedOpen(open => !open)}
            aria-expanded={advancedOpen}
            aria-controls="buddies-advanced"
          >
            <span>{t('buddiesSearch.advancedSettings')}</span>
            <ChevronDownIcon
              size={20}
              className={advancedOpen ? 'buddies-page__accordion-icon--open' : undefined}
            />
          </button>

          {advancedOpen && (
            <div className="buddies-page__advanced-body" id="buddies-advanced">
              <fieldset className="buddies-page__field">
                <legend>{t('basicInfo.currentWeight')}</legend>
                <UnitToggle
                  unit={weightUnit}
                  label={t('basicInfo.currentWeight')}
                  onChange={setWeightUnit}
                />
                <ScaleBar
                  mode="range"
                  min={weightBounds.min}
                  max={weightBounds.max}
                  value={weightDisplay}
                  onChange={([low, high]) =>
                    setWeightKg(isLbsWeight ? [lbsToKg(low), lbsToKg(high)] : [low, high])
                  }
                  formatValue={value => `${value} ${weightUnitLabel}`}
                  minAriaLabel={`${t('basicInfo.currentWeight')} ${t('common.minimum')}`}
                  maxAriaLabel={`${t('basicInfo.currentWeight')} ${t('common.maximum')}`}
                  ticks={weightTicks}
                />
              </fieldset>

              <fieldset className="buddies-page__field">
                <legend>{t('basicInfo.wantsToLose')}</legend>
                <div
                  className="buddies-page__chips"
                  role="group"
                  aria-label={t('basicInfo.wantsToLose')}
                >
                  {(['any', 'range'] as LoseMode[]).map(option => (
                    <button
                      key={option}
                      className={`buddies-page__chip${loseMode === option ? ' buddies-page__chip--selected' : ''}`}
                      type="button"
                      onClick={() => setLoseMode(option)}
                      aria-pressed={loseMode === option}
                    >
                      {t(`buddiesSearch.${option}`)}
                    </button>
                  ))}
                </div>

                {loseMode === 'range' && (
                  <div className="buddies-page__advanced-slider">
                    <UnitToggle
                      unit={loseUnit}
                      label={t('basicInfo.wantsToLose')}
                      onChange={setLoseUnit}
                    />
                    <ScaleBar
                      min={loseBounds.min}
                      max={loseBounds.max}
                      value={loseDisplay}
                      onChange={value => setLoseKg(isLbsLose ? lbsToKg(value) : value)}
                      formatValue={value => `${value} ${loseUnitLabel}`}
                      ariaLabel={t('basicInfo.wantsToLose')}
                      ticks={loseTicks}
                    />
                  </div>
                )}
              </fieldset>
            </div>
          )}
        </div>
      </div>}

      {!showResults && (
        <div className="buddies-page__actions">
          <button className="buddies-page__submit" type="button" onClick={showMatches} disabled={loading}>
            {loading ? t('home.buddies.loading') : t('home.buddies.showMatches')}
          </button>
          {activeFilterCount > 0 && (
            <button className="buddies-page__clear" type="button" onClick={clearFilters}>
              {t('buddiesSearch.clearFilters')}
            </button>
          )}
        </div>
      )}

      {!showResults && (
        <button
          className="buddies-page__robi"
          type="button"
          disabled={loading}
          onClick={() => handleAiSuggest().catch(() => undefined)}
        >
          {t('home.buddies.robiSuggestion')}
        </button>
      )}

      {/* The previous list is withheld while a new query is in flight so two
          searches are never on screen together. */}
      {showResults && loading && (
        <p className="buddies-page__empty" role="status">{t('home.buddies.loading')}</p>
      )}

      {showResults && !loading && (aiMode ? aiError : searchError) && (
        <ListStatus
          variant="error"
          message={(aiMode ? aiError : searchError) ?? undefined}
          onAction={() => {
            if (aiMode) {
              handleAiSuggest().catch(() => undefined);
            } else {
              const payload = lastSearchRef.current;
              if (payload) runSearch(payload).catch(() => undefined);
            }
          }}
          busy={loading}
        />
      )}

      {/* No refresh here: re-running the same criteria cannot invent matches. */}
      {showResults && !loading && !(aiMode ? aiError : searchError) && (aiMode ? aiMatches : results).length === 0 && (
        <ListStatus variant="empty" title={t('home.buddies.noMatches')} />
      )}

      {showResults && !loading && !(aiMode ? aiError : searchError) && !aiMode && results.length > 0 && (
        <div className="buddies-page__results">
          {visibleResults.map(match => {
            const isStatic = 'id' in match;
            const name = isStatic ? match.name : match.userName;
            const avatarUrl = isStatic ? null : resolveProfilePictureUrl(match.userPhoto);
            const fallback = avatarFallback({ seed: match.userGuid, name });
            const percentage = isStatic ? match.match : match.matchPercentage;
            const buddy = isStatic ? match : null;
            const user = isStatic ? null : match;
            const buttonState = user ? getBuddyRequestButtonState(user) : 'REQUEST';
            const requesting = user != null && requestingUserId === user.userId;
            const opening = user != null && openingUserId === String(user.userId);
            return (
              <article className="buddies-page__result" key={isStatic ? match.id : match.userId}>
                <button
                  type="button"
                  className="buddies-page__result-avatar-action"
                  aria-label={t('home.accessibility.openUserProfile', { username: name })}
                  onClick={() => openUserProfile(match.userGuid, { state: { isBuddyFlow: true } })}
                >
                  {avatarUrl ? (
                    <img className="buddies-page__result-avatar" src={avatarUrl} alt="" aria-hidden />
                  ) : (
                    <span
                      className="buddies-page__result-avatar"
                      data-avatar-tone={fallback.tone}
                      aria-hidden
                    >
                      {fallback.initial}
                    </span>
                  )}
                </button>
                <div className="buddies-page__result-main">
                  <div className="buddies-page__result-heading">
                    <button
                      type="button"
                      className="buddies-page__result-name-action"
                      onClick={() => openUserProfile(match.userGuid, { state: { isBuddyFlow: true } })}
                    >
                      {name}
                    </button>
                    <span className="buddies-page__match">{t('home.buddies.match', { percentage })}</span>
                  </div>
                  <p>{buddy?.bio ?? t('home.buddies.defaultBio')}</p>
                  <div className="buddies-page__result-meta">
                    <span>{t('home.buddies.ageMeta', { age: buddy?.age ?? 32 })}</span>
                    <span>{buddy?.country ?? t('home.buddies.defaultCountry')}</span>
                    <span>{t('home.buddies.weightMeta', { weight: isStatic ? match.weight : `${match.weightToLose} ${match.weightToLoseUnit}` })}</span>
                  </div>
                  <div className="buddies-page__result-actions">
                    <button type="button" onClick={() => openUserProfile(match.userGuid)}>{t('home.buddies.viewProfile')}</button>
                    {buttonState === 'ACCEPTED' && user ? (
                      <button
                        type="button"
                        disabled={opening}
                        aria-busy={opening}
                        onClick={() => {
                          openConversation({
                            userId: user.userId,
                            name: user.userName,
                            avatar: resolveProfilePictureUrl(user.userPhoto),
                          }).catch(() => undefined);
                        }}
                      >
                        {t(opening ? 'chat.opening' : 'common.message')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!user || buttonState === 'REQUESTED' || requesting}
                        aria-busy={requesting}
                        onClick={() => {
                          if (!user) return;
                          requestBuddy(user).catch(() => undefined);
                        }}
                      >
                        {t(
                          requesting
                            ? 'home.buddies.requesting'
                            : buttonState === 'REQUESTED'
                              ? 'home.buddies.requested'
                              : 'home.buddies.requestBuddy',
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          <InfiniteScrollFooter
            hasNextPage={hasMoreResults}
            isFetchingMore={expanding}
            onLoadMore={() => setExpanding(true)}
            itemCount={visibleResults.length}
          />
        </div>
      )}

      {showResults && !loading && !aiError && aiMode && aiMatches.length > 0 && (
        <ul className="ai-buddy-list" aria-label={t('aiBuddy.accessibility.list')}>
          {aiMatches.map((item, index) => {
            const buddy = mapAiMatchToBuddy(item);
            const selected = isCurrent(buddy);
            const score = item.score ?? 0;
            const itemKey = item.buddyId ?? item.userGuid ?? `ai-match-${index}`;
            return (
              <li
                key={itemKey}
                className={`ai-buddy-card${selected ? ' ai-buddy-card--current' : ''}`}
              >
                <div className="ai-buddy-card__header">
                  <Avatar
                    src={item.profilePictureUrl ? resolveProfilePictureUrl(item.profilePictureUrl) : null}
                    name={item.name ?? ''}
                    seed={item.userGuid ?? item.buddyId ?? ''}
                    size="lg"
                  />
                  <div className="ai-buddy-card__identity">
                    <p className="ai-buddy-card__name">{item.name}</p>
                    {item.about && <p className="ai-buddy-card__role">{item.about}</p>}
                  </div>
                  {score > 0 && (
                    <span className="ai-buddy-card__badge">
                      {t('buddiesSearch.matchPercent', { percent: score })}
                    </span>
                  )}
                </div>

                {item.description && (
                  <p className="ai-buddy-card__description">{item.description}</p>
                )}

                <div className="ai-buddy-card__actions">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openUserProfile(item.userGuid)}
                  >
                    {t('aiBuddy.list.explore')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={pendingBuddyId === buddy.id}
                    onClick={() => setAiTarget(buddy)}
                  >
                    {t(selected ? 'profile.buttons.unfollow' : 'aiBuddy.details.chooseButton')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {renderAiConfirmModal()}
      <ScrollToTopButton enabled={showResults && (aiMode ? aiMatches.length : results.length) > 0} />
    </section>
  );
}
